import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { KanoCategory, KanoPredictionResult } from '../../ai/useKanoPrediction';
import { theme } from '../../theme/theme';
import { typography } from '../../theme/typography';
import { KanoResultBox } from './KanoResultBox';

export interface KanoGridProps {
  result: KanoPredictionResult;
}

export const KanoGrid: React.FC<KanoGridProps> = ({ result }) => {
  const xAxisRef = useRef<HTMLDivElement>(null);
  const yAxisRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [categories, setCategories] = useState<KanoCategory[]>([]);

  useEffect(() => {
    const allCategories: KanoCategory[] = [
      { type: 'Attractive', features: [] },
      { type: 'Performance', features: [] },
      { type: 'Reverse', features: [] },
      { type: 'Must-Be', features: [] },
      { type: 'Indifferent', features: [] },
    ];

    const updatedCategories = allCategories.map(category => {
      const existingCategory = result.categories.find(c => c.type === category.type);
      return existingCategory ? existingCategory : category;
    });

    setCategories(updatedCategories);
  }, [result]);

  useEffect(() => {
    const updateWidth = () => {
      if (xAxisRef.current) {
        setContainerWidth(xAxisRef.current.offsetWidth);
      }
      if (yAxisRef.current) {
        setContainerHeight(yAxisRef.current.offsetHeight);
      }
    };

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    if (xAxisRef.current) {
      observer.observe(xAxisRef.current);
    }
    if (yAxisRef.current) {
      observer.observe(yAxisRef.current);
    }

    updateWidth();

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Container>
      <XAxis ref={xAxisRef} />
      <YAxis ref={yAxisRef} />
      <XLabel $left={true}>
        Verfehlt <br />
        Kundenanforderung
      </XLabel>
      <XLabel $left={false}>
        Erfüllt
        <br />
        Kundenanforderung
      </XLabel>
      <YLabel $top={true}>
        Erhöht <br />
        Kundenzufriedenheit
      </YLabel>
      <YLabel $top={false}>
        Verringert
        <br />
        Kundenzufriedenheit
      </YLabel>

      {categories.map((category, index) => (
        <KanoResultBox
          key={index}
          type={category.type}
          features={category.features}
          containerHeight={containerHeight}
          containerWidth={containerWidth}
        />
      ))}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  min-height: 600px;

  @media screen and (max-width: ${theme.responsive.breakpoints.medium}) {
    min-width: 1000px;
  }
`;

const Axis = styled.div`
  position: absolute;
  background-color: #b4b7cf;
  z-index: 1;
`;

const XAxis = styled(Axis)`
  top: 50%;
  left: 130px;
  right: 130px;
  height: 1px;
  transform: translateY(-50%);
`;

const YAxis = styled(Axis)`
  left: 50%;
  top: 40px;
  bottom: 40px;
  width: 1px;
  transform: translateX(-50%);
`;

const AxisLabel = styled.div`
  position: absolute;
  color: #000;
  text-align: center;
  ${typography('caption1')};
`;

const XLabel = styled(AxisLabel)<{ $left?: boolean }>`
  top: 50%;
  transform: translateY(-50%);
  ${({ $left: left }) => (left ? 'left: 0;' : 'right: 0;')}
  text-align: ${({ $left: left }) => (left ? 'right' : 'left')};
  width: 120px;
`;

const YLabel = styled(AxisLabel)<{ $top: boolean }>`
  left: 50%;
  transform: translateX(-50%);
  ${({ $top }) => ($top ? 'top: 0;' : 'bottom: 0;')}
  max-height: 30px;
`;
